/**
 * Bảng giá gói SAPA + tài khoản nhận tiền — MỘT nguồn (kế hoạch 10/2026 mục 5.6, chủ dự án chốt 10/10, ADR-094).
 *
 * Chủ tiệm thấy bảng giá và mã chuyển khoản ở Cấu hình → Gói SAPA. Không cổng thanh toán, không tự gia hạn
 * (NOT-DOING #24): chủ tiệm tự chuyển khoản, người vận hành ghi "Sổ thanh toán gói" ở bàn vận hành (ADR-070).
 *
 * Tiền là `bigint` đồng, số nguyên. KHÔNG nằm trong barrel `index.ts` (có literal `0n`; Mini App build ES2015 không
 * nhận) — nhập bằng `@sapa/shared/gia-sapa`.
 */
import { lamSachNoiDung, taoChuoiVietQr } from './vietqr.ts'

/**
 * SAPA đang MIỄN PHÍ cho mọi cửa hàng (chủ dự án 26/10/2026, ADR-113). Một công tắc trong mã, không xoá gì:
 *  - `true`  : ẩn bảng giá + mã chuyển khoản (màn Đăng nhập, Cấu hình → Gói SAPA); hết dùng thử / hết hạn KHÔNG còn đóng
 *              đặt lịch online — chỉ tiệm bị tạm ngưng (`status='suspended'`) mới đóng; dải "Gói SAPA còn N ngày" tự ẩn.
 *  - `false` : hiện lại bảng giá + chuyển khoản, và hết hạn lại đóng đặt lịch online (ADR-057).
 * ĐỔI VỀ `false` MỘT MÌNH SẼ ĐÓNG đặt lịch online của mọi tiệm đang hết hạn NGAY, không báo trước: PHẢI đọc ADR-113 mục
 * "Việc phải làm TRƯỚC khi bật lại thu phí" (báo trước ≥ 30 ngày, cộng ngày miễn phí vào hạn, điều khoản mục 6, rà tiệm ZBS) trước.
 * Muốn thu phí lại: đổi đúng một dòng này rồi deploy. Dữ liệu (`trial_ends_at`, `paid_until`, sổ thanh toán gói) không đổi.
 * Kiểu ghi rõ `boolean` để TypeScript không thu hẹp thành `true` rồi coi nhánh kia là không bao giờ chạy.
 */
export const SAPA_DANG_MIEN_PHI: boolean = true

/** Tài khoản nhận phí gói SAPA. Đổi tài khoản = sửa MỘT chỗ này, rồi `pnpm test` (có test cấu trúc mã VietQR). */
export const TAI_KHOAN_NHAN_SAPA = {
  nganHang: 'MB Bank',
  /** Mã BIN 6 số của MB (docs/vietqr.md — api.vietqr.io/v2/banks). */
  bin: '970422',
  soTaiKhoan: '0987939605',
  chuTaiKhoan: 'Ngô Văn Cường',
} as const

export const BANG_GIA_SAPA = {
  /** Giá niêm yết: mỗi cửa hàng mỗi tháng, KHÔNG chia mức theo số nhân viên. */
  giaThangNiemYetVnd: 149_000n,
  /** Giá cho tiệm thử kín, giữ nguyên `thangGiuGiaThuKin` tháng. */
  giaThangThuKinVnd: 99_000n,
  thangGiuGiaThuKin: 12,
  /** Tiệm thử kín được miễn phí bấy nhiêu tháng đầu. */
  thangMienPhiThuKin: 3,
  /** Đóng một lần `thangDongNam` tháng thì tặng thêm `thangTangKhiDongNam` tháng. */
  thangDongNam: 12,
  thangTangKhiDongNam: 2,
  /** Tin Zalo: nạp tối thiểu, SAPA bán đúng giá vốn (không cộng lãi). */
  tinZaloNapToiThieuVnd: 100_000n,
} as const

/** Tiền phải đóng khi đóng trọn `thangDongNam` tháng theo `giaThangVnd`. */
export function tienDongNamVnd(giaThangVnd: bigint): bigint {
  return giaThangVnd * BigInt(BANG_GIA_SAPA.thangDongNam)
}

/** Số tháng được dùng khi đóng một lần theo năm: đóng 12 tặng 2 → 14. */
export function thangDuocDungKhiDongNam(): number {
  return BANG_GIA_SAPA.thangDongNam + BANG_GIA_SAPA.thangTangKhiDongNam
}

/** Nội dung chuyển khoản: `SAPA {mã tiệm}` — bỏ dấu, tối đa 23 ký tự (mã tiệm 8 ký tự → vừa). */
export function noiDungChuyenKhoanSapa(maTiem: string): string {
  return lamSachNoiDung(`SAPA ${maTiem}`)
}

/**
 * Chuỗi VietQR "tĩnh" tới tài khoản SAPA, nội dung `SAPA {mã tiệm}`. Không điền sẵn số tiền: SAPA chưa biết tiệm thuộc
 * mức giá nào (niêm yết / thử kín / đóng năm) — chủ tiệm chọn theo bảng giá rồi nhập khi chuyển.
 * Mã tiệm rỗng / không phải chữ-số → `null` (không dựng mã nội dung mơ hồ mà người vận hành không đối soát được).
 */
export function chuoiVietQrSapa(maTiem: string): string | null {
  if (!/^[A-Za-z0-9]{4,12}$/.test(maTiem)) return null
  return taoChuoiVietQr({
    bin: TAI_KHOAN_NHAN_SAPA.bin,
    soTaiKhoan: TAI_KHOAN_NHAN_SAPA.soTaiKhoan,
    noiDung: noiDungChuyenKhoanSapa(maTiem),
  })
}
